import React, { useState, useEffect } from "react";
import { Form, Input, Select, DatePicker, Button, Row, Col } from "antd";
import dayjs from "dayjs";
import {
    SearchOutlined,
    ClearOutlined,
    FilterOutlined,
    UpOutlined,
    CalendarOutlined,
    IdcardOutlined,
    MailOutlined,
    PhoneOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
// Import shared maps to drive filter dropdowns — no hardcoded labels
import { SOURCE_MAP, TICKET_STATUS_MAP, PRIORITY_MAP } from "./crmConstants";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Quick filter button labels with day offsets for date filters and department filters
const QUICK_FILTERS = [
    { label: "Today", days: 0, type: "date" },
    { label: "Yesterday", days: 1, type: "date" },
    { label: "Last 3 Days", days: 3, type: "date" },
    { label: "Last 7 Days", days: 7, type: "date" },
    { label: "Sahara Voice Bot", type: "department", value: "Sahara Voice Bot" },
    { label: "Setu Chat Bot", type: "department", value: "Setu Chat Bot" },
    { label: "Chatbot", type: "department", value: "Chatbot" },
    { label: "😊 Satisfied", type: "satisfaction", value: [103] },
    { label: "😞 Not Satisfied", type: "satisfaction", value: [100] },
    { label: "📊 Has Feedback", type: "satisfaction", value: [100, 103] },
];

interface TicketFiltersProps {
    onFilter: (values: any) => void;
    onReset: () => void;
    loading: boolean;
    // Persisted filter values passed from parent to restore form state on back-navigation
    initialValues?: any;
    // Live-update props — show inline refresh CTA when background poll detects delta
    hasUpdates?: boolean;
    updateCount?: number;
    onRefresh?: () => void;
}

// Ticket filter panel with toggle to show/hide, compact input sizes, open by default like SaharaFilters
const TicketFilters = ({ onFilter, onReset, loading, initialValues, hasUpdates, updateCount, onRefresh }: TicketFiltersProps) => {
    const [form] = Form.useForm();
    // Track active quick filter label for highlighted pill styling
    const [activeQuick, setActiveQuick] = useState<string | null>(() => {
        return sessionStorage.getItem('freshdesk_quick_filter') || null;
    });
    // Filter panel is visible by default like SaharaFilters
    const [visible, setVisible] = useState(true);

    // Re-populate form fields with persisted ISO strings, converting to dayjs for antd v6 DatePicker
    useEffect(() => {
        if (!initialValues || Object.keys(initialValues).length === 0) return;
        const restoredValues: any = { ...initialValues };
        const dateFields = ["created_at", "updated_at", "due_by"] as const;
        dateFields.forEach((field) => {
            if (restoredValues[field] && Array.isArray(restoredValues[field])) {
                restoredValues[field] = [
                    dayjs(restoredValues[field][0]),
                    dayjs(restoredValues[field][1]),
                ];
            }
        });
        form.setFieldsValue(restoredValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Converts moment/dayjs date pairs to ISO strings — prevents raw date objects leaking into parent state
    const serializeValues = (values: any): any => {
        const dateFields = ['created_at', 'due_by', 'updated_at'];
        const result = { ...values };
        dateFields.forEach((field) => {
            if (result[field] && Array.isArray(result[field]) && result[field].length === 2) {
                const [start, end] = result[field];
                // Handle moment objects, dayjs objects, or already-ISO strings
                const toISO = (v: any): string | undefined => {
                    if (!v) return undefined;
                    if (typeof v === 'string') return v;
                    if (typeof v?.toISOString === 'function') return v.toISOString();
                    return undefined;
                };
                const s = toISO(start);
                const e = toISO(end);
                result[field] = s && e ? [s, e] : undefined;
            } else if (result[field] && !Array.isArray(result[field])) {
                result[field] = undefined; // clear malformed value
            }
        });
        return result;
    };

    // Apply quick filter by computing date range or department value and triggering fetch immediately
    const applyQuickFilter = (label: string, days?: number, type?: string, value?: string | number[]) => {
        setActiveQuick(label);
        sessionStorage.setItem('freshdesk_quick_filter', label);
        setVisible(true);

        // Serialize current form values so no raw moment objects leak into parent state
        const current = serializeValues(form.getFieldsValue());

        if (type === "date") {
            let startDay: dayjs.Dayjs;
            let endDay: dayjs.Dayjs;

            if (days === 0) {
                startDay = dayjs().startOf('day');
                endDay = dayjs().endOf('day');
            } else if (days === 1) {
                startDay = dayjs().subtract(1, 'day').startOf('day');
                endDay = dayjs().subtract(1, 'day').endOf('day');
            } else {
                startDay = dayjs().subtract(days || 0, 'day').startOf('day');
                endDay = dayjs().endOf('day');
            }

            // Set dayjs objects in the form — antd v6 DatePicker requires dayjs, not moment
            form.setFieldsValue({ created_at: [startDay, endDay] });

            onFilter({
                ...current,
                created_at: [startDay.toISOString(), endDay.toISOString()],
            });
        } else if (type === "department" && typeof value === 'string') {
            form.setFieldsValue({ department: value });
            onFilter({ ...current, department: value });
        } else if (type === "satisfaction" && Array.isArray(value)) {
            form.setFieldsValue({ customer_satisfaction_rating: value });
            onFilter({ ...current, customer_satisfaction_rating: value });
        }
    };

    // Converts form values to API-compatible filter object and calls parent handler — clear quick filter on manual filter apply
    const handleFinish = (values: any) => {
        setActiveQuick(null);
        sessionStorage.removeItem('freshdesk_quick_filter');
        onFilter(serializeValues(values));
    };

    // Resets all form fields and notifies parent with empty filters — clear quick filter state
    const handleReset = () => {
        setActiveQuick(null);
        sessionStorage.removeItem('freshdesk_quick_filter');
        form.resetFields();
        onReset();
    };

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-6">
            {/* Quick filter pill buttons row */}
            <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100">
                <span className="text-xs text-gray-500 font-medium mr-1">Quick:</span>
                {QUICK_FILTERS.map(({ label, days, type, value }) => (
                    <button
                        key={label}
                        onClick={() => applyQuickFilter(label, days, type, value)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer ${activeQuick === label
                            ? "bg-purple-600 text-white border-purple-600"
                            : "bg-white text-gray-600 border-gray-200 hover:border-purple-400 hover:text-purple-600"
                            }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Toggle header bar with filter label and expand/collapse button */}
            <div
                className="flex items-center justify-between px-5 py-3 cursor-pointer select-none hover:bg-gray-50 transition-colors"
                onClick={() => setVisible((v) => !v)}
            >
                <div className="flex items-center gap-2 text-gray-600 font-medium text-sm">
                    <FilterOutlined />
                    <span>Filters</span>
                </div>
                <UpOutlined
                    className="text-gray-400 text-xs transition-transform duration-300"
                    style={{ transform: visible ? "rotate(0deg)" : "rotate(180deg)" }}
                />
            </div>

            {/* Collapsible filter form panel, hidden by default */}
            {visible && (
                <Form
                    form={form}
                    onFinish={handleFinish}
                    layout="vertical"
                    className="px-5 pt-4 pb-3 border-t border-gray-100"
                    size="small"
                >
                    <Row gutter={[16, 4]}>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            {/* User ID filter maps to requester_userid in POST body */}
                            <Form.Item name="requester_userid" label="User ID" style={{ marginBottom: 12 }}>
                                <Input
                                    placeholder="Enter User ID"
                                    prefix={<IdcardOutlined className="text-gray-400" />}
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="ticket_id" label="Ticket ID" style={{ marginBottom: 12 }}>
                                <Input
                                    placeholder="Enter Ticket ID"
                                    prefix={<IdcardOutlined className="text-gray-400" />}
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="subject" label="Subject" style={{ marginBottom: 12 }}>
                                <Input
                                    placeholder="Enter Subject"
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="requester_email" label="Requester Email" style={{ marginBottom: 12 }}>
                                <Input
                                    placeholder="Enter Requester Email"
                                    prefix={<MailOutlined className="text-gray-400" />}
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="requester_phone" label="Requester Phone" style={{ marginBottom: 12 }}>
                                <Input
                                    placeholder="Enter Requester Phone"
                                    prefix={<PhoneOutlined className="text-gray-400" />}
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="status" label="Status" style={{ marginBottom: 12 }}>
                                <Select mode="multiple" placeholder="Select Status">
                                    {Object.entries(TICKET_STATUS_MAP).map(([code, label]) => (
                                        <Option key={code} value={Number(code)}>{label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="priority" label="Priority" style={{ marginBottom: 12 }}>
                                <Select mode="multiple" placeholder="Select Priority">
                                    {Object.entries(PRIORITY_MAP).map(([code, label]) => (
                                        <Option key={code} value={Number(code)}>{label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                            <Form.Item name="created_at" label="Created At" style={{ marginBottom: 12 }}>
                                <RangePicker
                                    style={{ width: "100%" }}
                                    suffixIcon={<CalendarOutlined className="text-gray-400" />}
                                    onChange={() => {
                                        setActiveQuick(null);
                                        sessionStorage.removeItem('freshdesk_quick_filter');
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                            <Form.Item name="due_by" label="Due By" style={{ marginBottom: 12 }}>
                                <RangePicker
                                    style={{ width: "100%" }}
                                    suffixIcon={<CalendarOutlined className="text-gray-400" />}
                                    onChange={() => {
                                        setActiveQuick(null);
                                        sessionStorage.removeItem('freshdesk_quick_filter');
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="agent_name" label="Agent Name" style={{ marginBottom: 12 }}>
                                <Select
                                    mode="multiple"
                                    placeholder="Select Agent"
                                    allowClear
                                    maxTagCount="responsive"
                                >
                                    <Option value="ChatBot Agent">ChatBot Agent</Option>
                                    <Option value="Sahara Voice Bot">Sahara Voice Bot</Option>
                                    <Option value="Setu Chat Bot">Setu Chat Bot</Option>
                                    <Option value="Anuj">Anuj</Option>
                                    <Option value="Bharat">Bharat</Option>
                                    <Option value="Kundan">Kundan</Option>
                                    <Option value="Raja">Raja</Option>
                                    <Option value="Shweta">Shweta</Option>
                                    <Option value="Vikash">Vikash</Option>
                                    <Option value="Yogesh">Yogesh</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            {/* Department filter for Portal tickets */}
                            <Form.Item name="department" label="Department" style={{ marginBottom: 12 }}>
                                <Select placeholder="Select Department" allowClear>
                                    <Option value="Sahara Voice Bot">Sahara Voice Bot</Option>
                                    <Option value="Setu Chat Bot">Setu Chat Bot</Option>
                                    <Option value="Chatbot">Chatbot</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="source" label="Source" style={{ marginBottom: 12 }}>
                                {/* Source options generated from SOURCE_MAP */}
                                <Select placeholder="Select Source" allowClear>
                                    {Object.entries(SOURCE_MAP).map(([code, label]) => (
                                        <Option key={code} value={Number(code)}>{label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="category_code" label="Category Code" style={{ marginBottom: 12 }}>
                                <Input
                                    placeholder="e.g. 230000000"
                                    prefix={<SearchOutlined className="text-gray-400" />}
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="customer_satisfaction_rating" label="Customer Satisfaction" style={{ marginBottom: 12 }}>
                                <Select
                                    mode="multiple"
                                    placeholder="Select satisfaction level"
                                    allowClear
                                    maxTagCount="responsive"
                                >
                                    <Option value={103}>😊 Satisfied</Option>
                                    <Option value={100}>😞 Not Satisfied</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Action buttons row — Reset | [live CTA] | Filter */}
                    <div className="flex justify-end items-center gap-2 pt-1 pb-1 border-t border-gray-50 mt-2">
                        <Button size="small" icon={<ClearOutlined />} onClick={handleReset}>
                            Reset
                        </Button>

                        {/* Compact live-update CTA shown inline when poll detects changes */}
                        {hasUpdates && (
                            <>
                                <style>{`
                                    @keyframes tf-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                                    @keyframes tf-glow {
                                        0%,100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.5); }
                                        50%      { box-shadow: 0 0 0 5px rgba(99,102,241,0); }
                                    }
                                    .tf-live-cta { animation: tf-glow 1.8s ease-in-out infinite; }
                                    .tf-spin-icon { animation: tf-spin 1.1s linear infinite; display: inline-flex; }
                                `}</style>
                                <button
                                    onClick={onRefresh}
                                    className="tf-live-cta inline-flex items-center gap-1.5 border-0 outline-none cursor-pointer"
                                    style={{
                                        background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                                        color: '#fff',
                                        borderRadius: 20,
                                        padding: '2px 10px',
                                        fontSize: 11,
                                        fontWeight: 600,
                                        letterSpacing: '0.1px',
                                        height: 24,
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={`${updateCount} ticket(s) changed — click to refresh`}
                                >
                                    <span className="tf-spin-icon">
                                        <ReloadOutlined style={{ fontSize: 10 }} />
                                    </span>
                                    {updateCount} update{(updateCount ?? 0) > 1 ? 's' : ''}
                                </button>
                            </>
                        )}

                        <Button
                            size="small"
                            type="primary"
                            htmlType="submit"
                            icon={<SearchOutlined />}
                            loading={loading}
                            className="bg-purple-600 hover:bg-purple-500 border-purple-600"
                        >
                            Filter
                        </Button>
                    </div>
                </Form>
            )}
        </div>
    );
};

export default TicketFilters;
